import { useMemo } from 'react';

import {
  Anchor,
  ComboboxItem,
  ComboboxItemGroup,
  Group,
  OptionsFilter,
  Select,
  Text
} from '@mantine/core';
import { IconFilter, IconSearch } from '@tabler/icons-react';

import poc from '@/styles/pocScope.module.scss';

import styles from './CreatePatientModal.module.scss';

import type { Organization } from '@/types/organization';

type ClinicLike = Pick<Organization, 'id' | 'name'> & {
  parent?: Pick<Organization, 'id' | 'name'> | null;
};

interface ClinicSelectProps {
  /** Active clinics the user can add to. */
  clinics: ClinicLike[];
  /** Active clinics in the page's clinic filter; empty when the page isn't filtered. */
  filteredClinics: ClinicLike[];
  /** Show only `filteredClinics` (true while the page filter applies). */
  useFilter: boolean;
  onUseFilterChange: (useFilter: boolean) => void;
  value: string;
  onChange: (clinicId: string) => void;
  error?: string;
}

const NO_ACCOUNT = 'Other clinics';

/** Clinics grouped by account, sorted by account then clinic. */
export function groupClinics(clinics: ClinicLike[]): ComboboxItemGroup<ComboboxItem>[] {
  const groups = new Map<string, ComboboxItem[]>();
  clinics.forEach((clinic) => {
    const account = clinic.parent?.name?.trim() || NO_ACCOUNT;
    const items = groups.get(account) ?? [];
    items.push({ value: clinic.id, label: clinic.name });
    groups.set(account, items);
  });
  return [...groups.entries()]
    .sort(([a], [b]) => (a === NO_ACCOUNT ? 1 : b === NO_ACCOUNT ? -1 : a.localeCompare(b)))
    .map(([group, items]) => ({
      group,
      items: items.sort((a, b) => a.label.localeCompare(b.label))
    }));
}

/** Matches the clinic name or the account name; an account match keeps all its clinics. */
export const clinicFilter: OptionsFilter = ({ options, search }) => {
  const query = search.trim().toLowerCase();
  if (!query) return options;
  return (options as ComboboxItemGroup<ComboboxItem>[])
    .map((group) => {
      if (group.group.toLowerCase().includes(query)) return group;
      return {
        ...group,
        items: group.items.filter((item) => item.label.toLowerCase().includes(query))
      };
    })
    .filter((group) => group.items.length > 0);
};

/**
 * Clinic picker for Add Patient: active clinics only, searchable by clinic or account, grouped by
 * account. Starts from the patient list's clinic filter when one is applied, with a link to show
 * every clinic. One clinic: shown, not chosen.
 */
const ClinicSelect = ({
  clinics,
  filteredClinics,
  useFilter,
  onUseFilterChange,
  value,
  onChange,
  error
}: ClinicSelectProps) => {
  const filterApplies = filteredClinics.length > 0;
  const shown = filterApplies && useFilter ? filteredClinics : clinics;
  const data = useMemo(() => groupClinics(shown), [shown]);
  const only = clinics.length === 1 ? clinics[0] : null;

  if (only) {
    return (
      <div>
        <Text className={styles.fieldLabel}>Clinic</Text>
        <Text size="sm">
          {only.name}
          {only.parent?.name ? (
            <Text span c="dimmed" size="sm">
              {' '}
              · {only.parent.name}
            </Text>
          ) : null}
        </Text>
      </div>
    );
  }

  return (
    <div>
      <Select
        label="Clinic"
        withAsterisk
        placeholder="Search by clinic or account"
        searchable
        data={data}
        value={value || null}
        onChange={(next) => onChange(next ?? '')}
        filter={clinicFilter}
        nothingFoundMessage="No active clinic matches"
        maxDropdownHeight={320}
        leftSection={<IconSearch size={14} />}
        comboboxProps={{ withinPortal: true, zIndex: 1300, classNames: { dropdown: poc.pocScope } }}
        error={error}
        allowDeselect={false}
      />
      {filterApplies && (
        <Group gap={6} mt={6} wrap="nowrap">
          <IconFilter size={13} color="var(--poc-muted, #6b728a)" />
          <Text size="xs" c="dimmed">
            {useFilter
              ? `Showing ${filteredClinics.length} of ${clinics.length} clinics from your patient list filter.`
              : `Showing all ${clinics.length} active clinics.`}
          </Text>
          <Anchor
            component="button"
            type="button"
            size="xs"
            onClick={() => onUseFilterChange(!useFilter)}>
            {useFilter ? 'Show all clinics' : 'Use my filter'}
          </Anchor>
        </Group>
      )}
    </div>
  );
};

export default ClinicSelect;
